import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Check, PlayCircle, MessageCircle, Users, CreditCard, Loader2 } from 'lucide-react';
import { useUsage } from '../context/UsageContext';
import { useFreemium } from '../context/FreemiumContext';
import api from '../lib/api';
import { toast } from 'sonner';

const WA_NUM = '56998759833';
const YT_TUTORIAL_COMPLETO = 'https://youtu.be/XGkWWNbF_Y8?si=Qus9XE1bYAdcwtFZ';

function waLink(msg) {
    return `https://wa.me/${WA_NUM}?text=${encodeURIComponent(msg)}`;
}

const PLAN_A_FEATURES = [
    'Acceso completo a la plataforma',
    'IA especializada para evaluar las tareas del Portafolio',
    'Rúbricas Oficiales CPEIP 2026',
    'Análisis ilimitados',
    'Estrategia personalizada',
    'Guardado permanente de borradores',
    'Privacidad de la información',
    'Tutorial completo pregrabado',
    'Acceso al estudio de resultados históricos del Portafolio Docente, organizado por módulos y tareas',
];

export default function ActivarPlan() {
    const { planTipo, planActivo } = useUsage();
    const { isAdmin } = useFreemium();
    const isPlataforma = planTipo === 'plataforma' && planActivo;
    const isPremium = planTipo === 'premium' && planActivo; // usuarios históricos con Premium vigente
    const [mpCfg, setMpCfg] = React.useState(null);
    const [payingPlan, setPayingPlan] = React.useState(null);
    const [testPaying, setTestPaying] = React.useState(false);

    React.useEffect(() => {
        let alive = true;
        api.get('/mp/config').then((r) => { if (alive) setMpCfg(r.data); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    const iniciarPagoMP = async (plan) => {
        setPayingPlan(plan);
        try {
            const r = await api.post('/mp/preferences', { plan });
            const url = r.data?.init_point || r.data?.sandbox_init_point;
            if (!url) throw new Error('Preferencia sin init_point');
            window.location.assign(url);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = detail?.mensaje || e?.message || 'No pudimos iniciar el pago con Mercado Pago.';
            toast.error(msg);
            setPayingPlan(null);
        }
    };

    // Botón exclusivo para administradores: Checkout en entorno de PRUEBA.
    // Nunca activa derechos productivos. Usa `init_point` con credenciales TEST
    // (MP descontinuó `sandbox_init_point`; el modo TEST lo garantiza el token).
    const iniciarPagoMPTest = async (plan) => {
        setTestPaying(true);
        try {
            const r = await api.post('/mp/preferences-test', { plan });
            const url = r.data?.init_point;
            if (!url) throw new Error('Preferencia TEST sin init_point');
            window.location.assign(url);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = detail?.mensaje || detail?.code || e?.message || 'No pudimos iniciar el Checkout de prueba.';
            toast.error(msg);
            setTestPaying(false);
        }
    };

    return (
        <Layout>
            <div className="mx-auto max-w-6xl px-4 py-10 lg:px-8">
                {/* Hero */}
                <div className="text-center max-w-3xl mx-auto">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">Activar Plan</span>
                    <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                        {isPlataforma || isPremium
                            ? 'Ya tienes tu plan activo. Aquí verás cómo se muestra la página para nuevos docentes.'
                            : 'Ahora que conoces la plataforma, activa tu acceso completo.'}
                    </h1>
                    <p className="mt-4 text-slate-700 leading-relaxed">
                        Portafolio Digital Docente no promete resultados: te acompaña a comprender las rúbricas oficiales,
                        organizar tu trabajo y tomar mejores decisiones durante todo el proceso.
                    </p>
                </div>

                {/* ADMIN ONLY · Botón Modo Prueba Mercado Pago (sandbox aislado) */}
                {isAdmin && mpCfg && (
                    <div className="mt-6 rounded-xl border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/40 p-4"
                         data-testid="mp-test-admin-block">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-700">
                                    Solo administradores · Entorno TEST
                                </div>
                                <div className="font-semibold text-slate-900 mt-1">
                                    Modo Prueba Mercado Pago
                                </div>
                                <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                                    Abre Checkout en sandbox aislado. No modifica pagos, planes ni derechos productivos.
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-fuchsia-400 text-fuchsia-800 hover:bg-fuchsia-100"
                                    onClick={() => iniciarPagoMPTest('plataforma')}
                                    disabled={testPaying}
                                    data-testid="mp-test-plataforma-btn"
                                >
                                    {testPaying ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />}
                                    TEST · Plataforma
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Aviso para usuarios históricos con plan vigente */}
                {isPlataforma && (
                    <div className="mt-8 rounded-2xl border-2 border-sky-300 bg-sky-50 p-5 text-center" data-testid="already-plataforma-card">
                        <p className="text-sm text-sky-900">
                            ✅ Ya cuentas con el <strong>Plan Plataforma</strong> activo. Tu acceso completo está vigente.
                        </p>
                    </div>
                )}
                {isPremium && (
                    <div className="mt-8 rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 text-center" data-testid="already-premium-card">
                        <p className="text-sm text-emerald-900">
                            ✅ Tu plan contratado anteriormente sigue <strong>vigente</strong>. Conservas todos tus accesos.
                        </p>
                    </div>
                )}

                {/* Card única — Plan Plataforma */}
                <div className="mt-10 max-w-xl mx-auto">
                    <div className="rounded-2xl border-2 border-sky-200 bg-white p-6 sm:p-8 shadow-lg" data-testid="plan-a-card">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">Plan Plataforma</div>
                        <h2 className="mt-2 font-heading text-2xl font-bold text-slate-900">Plataforma Portafolio Digital Docente</h2>
                        <div className="mt-4 flex items-baseline gap-1">
                            <span className="font-mono text-4xl font-extrabold text-slate-900">$49.990</span>
                            <span className="text-sm text-slate-500">CLP · pago único</span>
                        </div>
                        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                            Ideal para docentes que desean desarrollar su Portafolio utilizando las herramientas
                            especializadas de Portafolio Digital Docente.
                        </p>
                        <ul className="mt-5 space-y-2">
                            {PLAN_A_FEATURES.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                                    <Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-6 space-y-2">
                            {mpCfg?.configured && (
                                <Button
                                    onClick={() => iniciarPagoMP('plataforma')}
                                    disabled={payingPlan === 'plataforma'}
                                    className="w-full bg-sky-600 hover:bg-sky-700 gap-2"
                                    data-testid="mp-pay-plataforma-btn"
                                >
                                    {payingPlan === 'plataforma' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <CreditCard className="h-4 w-4" />
                                    )}
                                    Pagar con Mercado Pago
                                </Button>
                            )}
                            <a
                                href={waLink('Hola. Deseo contratar el Plan Plataforma Portafolio Digital Docente.')}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="activar-plan-a-btn"
                            >
                                <Button variant={mpCfg?.configured ? "outline" : "default"} className={mpCfg?.configured ? "w-full gap-2" : "w-full bg-slate-900 hover:bg-slate-800 gap-2"}>
                                    <MessageCircle className="h-4 w-4" />
                                    {mpCfg?.configured ? 'Coordinar por WhatsApp' : 'Activar Plan Plataforma'}
                                </Button>
                            </a>
                            <p className="mt-2 text-[11px] text-slate-500 text-center">
                                {mpCfg?.configured
                                    ? 'Pago 100% online con Mercado Pago. Activación automática.'
                                    : 'Al hacer clic se abre WhatsApp con tu solicitud pre-cargada.'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tutorial */}
                <div className="mt-14 rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8 text-center">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-700 mb-3">
                        <PlayCircle className="h-6 w-6" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-slate-900">¿Aún no decides?</h3>
                    <p className="mt-2 text-sm text-slate-700 max-w-2xl mx-auto leading-relaxed">
                        Te invitamos primero a conocer cómo funciona Portafolio Digital Docente. En este tutorial de
                        aproximadamente 1 hora aprenderás cómo comenzar tu Portafolio, crear borradores, utilizar la IA,
                        interpretar los diagnósticos y organizar tu estrategia de trabajo.
                    </p>

                    {/* Miniatura de video estilo YouTube */}
                    <a
                        href={YT_TUTORIAL_COMPLETO}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Abrir tutorial completo en YouTube"
                        data-testid="tutorial-yt-btn"
                        className="group mt-6 mx-auto block w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md ring-1 ring-slate-100 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-2xl hover:ring-red-200"
                    >
                        <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
                            <img
                                src="/assets/tutorial-thumbnail.png"
                                alt="Miniatura tutorial Portafolio Digital Docente 2026"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                            />
                            {/* Overlay play button */}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/15">
                                <div className="flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-red-600/95 text-white shadow-2xl ring-4 ring-white/30 transition-transform duration-300 group-hover:scale-110">
                                    <PlayCircle className="h-8 w-8 sm:h-10 sm:w-10 fill-current" />
                                </div>
                            </div>
                        </div>
                    </a>

                    <p className="mt-3 text-[11px] text-slate-500">Duración aproximada: 1 hora · YouTube</p>
                </div>

                {/* Filosofía */}
                <div className="mt-10 rounded-2xl bg-slate-900 p-6 sm:p-8 text-white text-center">
                    <Users className="h-6 w-6 mx-auto opacity-75" />
                    <p className="mt-3 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto text-slate-200">
                        Portafolio Digital Docente no escribe el Portafolio por ti. Te acompaña con Inteligencia Artificial
                        entrenada en fuentes oficiales del CPEIP y con profesionales comprometidos con tu desarrollo.
                        La mejor inteligencia sigue siendo la del profesor o profesora que conoce a sus estudiantes.
                    </p>
                    <div className="mt-5">
                        <Link to="/dashboard">
                            <Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10">
                                Volver al Dashboard
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
